import { useCallback, useEffect, useRef } from 'react';

// Position readout for the time-shift scrubber. The native <video controls>
// timeline cannot carry our own tooltip (it lives in the browser's shadow DOM
// and Safari/Chrome disagree on what they show), so time-shift gets this bar
// instead -- one implementation, identical on desktop and touch.
//
// Everything that moves per pointer event or per frame is written straight to
// the DOM through refs. An earlier custom scrubber round-tripped each seek
// through React state and that is what produced the visible
// "0:00 -> Loading Video -> jump" stutter, so state is deliberately not used
// on the hot path; the actual seek is committed once, on release.

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const formatClock = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
};

export default function TimeshiftSeekBar({
  videoRef,
  shakaPlayerRef,
  onFullscreen,
  visible = true,
  // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: optional
  // (mediaSeconds, { kind: 'position' | 'edge' | 'tip' }) => text, given the
  // media time itself (window start + offset); a falsy result falls back to
  // formatClock(offset). A Live time-shift passes programme time.
  clockLabel = null,
  // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: optional () => media seconds, the
  // latest point a pointer may select (a Live time-shift keeps its floor
  // behind the live edge). A non-finite result means no limit of ours.
  latestSeekTime = null,
}) {
  const trackRef = useRef(null);
  const fillRef = useRef(null);
  const thumbRef = useRef(null);
  const tooltipRef = useRef(null);
  const elapsedRef = useRef(null);
  const totalRef = useRef(null);
  const playIconRef = useRef(null);
  const muteIconRef = useRef(null);
  const draggingRef = useRef(false);
  // DISPATCHARR_SEEKBAR_VOLUME_R3
  const volumeWrapRef = useRef(null);
  const volumeSlotRef = useRef(null);
  const volumeBarRef = useRef(null);
  const volumeFillRef = useRef(null);
  const volumeKnobRef = useRef(null);
  const volumeDraggingRef = useRef(false);
  const lastVolumeRef = useRef(1);
  // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: read at paint time, so the
  // memoized painters never hold a stale label function.
  const clockLabelRef = useRef(clockLabel);
  clockLabelRef.current = clockLabel;
  const latestSeekTimeRef = useRef(latestSeekTime);
  latestSeekTimeRef.current = latestSeekTime;
  const hoverClientXRef = useRef(null);
  const labelFor = (offsetSeconds, mediaSeconds, kind) => {
    const custom = clockLabelRef.current;
    if (typeof custom === 'function' && Number.isFinite(mediaSeconds)) {
      try {
        const text = custom(mediaSeconds, { kind });
        if (text) return text;
      } catch {
        // fall back to the plain clock
      }
    }
    return formatClock(offsetSeconds);
  };

  // Window comes from Shaka's manifest when it is driving playback; Safari's
  // video.seekable only covers roughly what is buffered.
  const readWindow = useCallback(() => {
    const video = videoRef.current;
    if (!video) return null;
    try {
      const player = shakaPlayerRef?.current;
      const range = player ? player.seekRange() : null;
      if (
        range &&
        Number.isFinite(range.start) &&
        Number.isFinite(range.end) &&
        range.end > range.start
      ) {
        return { start: range.start, end: range.end };
      }
    } catch {
      // fall through to the native range
    }
    if (video.seekable && video.seekable.length > 0) {
      const start = video.seekable.start(0);
      const end = video.seekable.end(video.seekable.length - 1);
      if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
        return { start, end };
      }
    }
    return null;
  }, [videoRef, shakaPlayerRef]);

  const timeAtClientX = useCallback(
    (clientX) => {
      const track = trackRef.current;
      const win = readWindow();
      if (!track || !win) return null;
      const rect = track.getBoundingClientRect();
      if (rect.width <= 0) return null;
      let ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
      // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: readout, thumb and commit all stop
      // at the limit, so what the pointer shows is what the release plays.
      const limit = latestSeekTimeRef.current;
      if (typeof limit === 'function') {
        let latest = NaN;
        try {
          latest = Number(limit());
        } catch {
          latest = NaN;
        }
        if (Number.isFinite(latest) && latest < win.end) {
          ratio = Math.min(ratio, clamp((latest - win.start) / (win.end - win.start), 0, 1));
        }
      }
      return {
        ratio,
        absolute: win.start + ratio * (win.end - win.start),
        offset: ratio * (win.end - win.start),
      };
    },
    [readWindow]
  );

  const paint = useCallback(
    (ratio, offsetSeconds, mediaSeconds) => {
      const pct = `${(ratio * 100).toFixed(3)}%`;
      if (fillRef.current) fillRef.current.style.width = pct;
      if (thumbRef.current) thumbRef.current.style.left = pct;
      if (elapsedRef.current) {
        elapsedRef.current.textContent = labelFor(offsetSeconds, mediaSeconds, 'position');
      }
    },
    []
  );

  // Volume: the icon reflects the level and the strip sets it. Like everything
  // else on this bar the updates go straight to the DOM through refs.
  // DISPATCHARR_SEEKBAR_VOLUME_R3
  const paintVolume = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const level = video.muted ? 0 : clamp(video.volume, 0, 1);
    const pct = `${(level * 100).toFixed(1)}%`;
    if (volumeFillRef.current) volumeFillRef.current.style.width = pct;
    if (volumeKnobRef.current) volumeKnobRef.current.style.left = pct;
    if (muteIconRef.current) {
      muteIconRef.current.textContent =
        level === 0 ? '🔇' : level < 0.34 ? '🔈' : level < 0.67 ? '🔉' : '🔊';
    }
  }, [videoRef]);

  const applyVolume = useCallback(
    (value) => {
      const video = videoRef.current;
      if (!video) return;
      const level = clamp(value, 0, 1);
      video.volume = level;
      // A real volume control lifts mute as soon as the level leaves zero, and
      // mutes on the way down instead of leaving a silent-but-unmuted player.
      video.muted = level === 0;
      if (level > 0) lastVolumeRef.current = level;
      paintVolume();
    },
    [videoRef, paintVolume]
  );

  const setVolumeSlotOpen = useCallback((open) => {
    const slot = volumeSlotRef.current;
    // DISPATCHARR_FULLSCREEN_CONTROL_SIZES_R122: fullscreen widens the strip
    // through this variable (FloatingVideo's stage CSS); 54px otherwise.
    if (slot) {
      slot.style.width = open
        ? 'var(--dispatcharr-seekbar-volume-open, 54px)'
        : '0px';
    }
  }, []);

  const volumeFromClientX = useCallback((clientX) => {
    const bar = volumeBarRef.current;
    if (!bar) return null;
    const rect = bar.getBoundingClientRect();
    if (rect.width <= 0) return null;
    return clamp((clientX - rect.left) / rect.width, 0, 1);
  }, []);

  const handleVolumePointerDown = (event) => {
    event.stopPropagation();
    volumeDraggingRef.current = true;
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // capture is optional
    }
    const level = volumeFromClientX(event.clientX);
    if (level != null) applyVolume(level);
  };

  const handleVolumePointerMove = (event) => {
    if (!volumeDraggingRef.current) return;
    const level = volumeFromClientX(event.clientX);
    if (level != null) applyVolume(level);
  };

  const handleVolumePointerUp = (event) => {
    if (!volumeDraggingRef.current) return;
    volumeDraggingRef.current = false;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // ignore
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.muted || video.volume === 0) {
      // Unmuting a player parked at zero has to restore something audible.
      applyVolume(lastVolumeRef.current > 0 ? lastVolumeRef.current : 1);
    } else {
      lastVolumeRef.current = video.volume;
      video.muted = true;
      paintVolume();
    }
  };

  // Wheel over the volume group nudges the level. Registered natively because
  // it must not be passive: it cancels the page scroll.
  useEffect(() => {
    const wrap = volumeWrapRef.current;
    if (!wrap) return undefined;
    const onWheel = (event) => {
      const video = videoRef.current;
      if (!video) return;
      event.preventDefault();
      const current = video.muted ? 0 : video.volume;
      applyVolume(current + (event.deltaY < 0 ? 0.05 : -0.05));
    };
    wrap.addEventListener('wheel', onWheel, { passive: false });
    return () => wrap.removeEventListener('wheel', onWheel);
  }, [videoRef, applyVolume]);

  // Follow playback without re-rendering React on every tick.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const sync = () => {
      if (draggingRef.current) return;
      const win = readWindow();
      if (!win) return;
      const span = win.end - win.start;
      const offset = clamp((video.currentTime || 0) - win.start, 0, span);
      paint(span > 0 ? offset / span : 0, offset, win.start + offset);
      if (totalRef.current) {
        totalRef.current.textContent = labelFor(span, win.end, 'edge');
      }
      // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: the window keeps growing
      // under a still pointer, so the readout follows what a click there
      // would commit.
      const hoverX = hoverClientXRef.current;
      if (hoverX !== null) {
        const at = timeAtClientX(hoverX);
        if (at) showTooltip(hoverX, at.offset, at.absolute);
      }
    };
    const syncPlayState = () => {
      if (playIconRef.current) {
        playIconRef.current.textContent = video.paused ? '▶' : '❚❚';
      }
    };

    sync();
    syncPlayState();
    paintVolume();
    video.addEventListener('volumechange', paintVolume);
    video.addEventListener('timeupdate', sync);
    video.addEventListener('progress', sync);
    video.addEventListener('durationchange', sync);
    video.addEventListener('play', syncPlayState);
    video.addEventListener('pause', syncPlayState);
    const ticker = window.setInterval(sync, 500);
    return () => {
      video.removeEventListener('volumechange', paintVolume);
      video.removeEventListener('timeupdate', sync);
      video.removeEventListener('progress', sync);
      video.removeEventListener('durationchange', sync);
      video.removeEventListener('play', syncPlayState);
      video.removeEventListener('pause', syncPlayState);
      window.clearInterval(ticker);
    };
    // showTooltip is declared below (a deps entry would hit its TDZ during
    // render); it is a stable useCallback([]), so the closure is enough.
  }, [videoRef, readWindow, paint, paintVolume, timeAtClientX]);

  const showTooltip = useCallback((clientX, offsetSeconds, mediaSeconds) => {
    const track = trackRef.current;
    const tip = tooltipRef.current;
    if (!track || !tip) return;
    const rect = track.getBoundingClientRect();
    tip.textContent = labelFor(offsetSeconds, mediaSeconds, 'tip');
    // Keep the whole readout over the track: translateX(-50%) would push
    // half of it past either end. DISPATCHARR_SEEKBAR_TOOLTIP_CLAMP_R1
    const half = (tip.offsetWidth || 0) / 2;
    const x = clamp(clientX - rect.left, half, Math.max(half, rect.width - half));
    tip.style.left = `${x}px`;
    tip.style.opacity = '1';
  }, []);

  const hideTooltip = useCallback(() => {
    hoverClientXRef.current = null;
    if (tooltipRef.current) tooltipRef.current.style.opacity = '0';
  }, []);

  const handlePointerMove = (event) => {
    const at = timeAtClientX(event.clientX);
    if (!at) return;
    if (event.pointerType === 'mouse') hoverClientXRef.current = event.clientX;
    showTooltip(event.clientX, at.offset, at.absolute);
    if (draggingRef.current) paint(at.ratio, at.offset, at.absolute);
  };

  const commit = (clientX) => {
    const at = timeAtClientX(clientX);
    const video = videoRef.current;
    if (at && video) {
      try {
        video.currentTime = at.absolute;
      } catch {
        // a seek can be rejected while the window is still settling
      }
    }
  };

  const handlePointerDown = (event) => {
    event.stopPropagation();
    draggingRef.current = true;
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // capture is optional
    }
    handlePointerMove(event);
  };

  const handlePointerUp = (event) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // ignore
    }
    commit(event.clientX);
    // Touch has no hover state to fall back to.
    if (event.pointerType !== 'mouse') hideTooltip();
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  return (
    <div
      className="floating-video-no-drag dispatcharr-fs-tsbar"
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 33,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        // Head room for the hover readout, which sits above the track, so it
        // fits inside this bar's own box. Clipping has to stay on: without it
        // the mute/fullscreen buttons spill outside a 220px-wide player
        // (MIN_WIDTH in FloatingVideo). DISPATCHARR_SEEKBAR_TOOLTIP_HEADROOM_R2
        padding: '24px 12px 12px',
        overflow: 'hidden',
        background:
          'linear-gradient(transparent, rgba(0,0,0,0.88) 35%, rgba(0,0,0,0.94))',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 0.25s ease',
      }}
    >
      <button
        type="button"
        className="dispatcharr-fs-btn dispatcharr-fs-play"
        title="Play/Pause"
        onClick={togglePlay}
        style={{
          border: 0,
          background: 'transparent',
          color: 'white',
          cursor: 'pointer',
          fontSize: 18,
          minWidth: 32,
          lineHeight: 1,
        }}
      >
        <span ref={playIconRef}>❚❚</span>
      </button>

      <span
        ref={elapsedRef}
        className="dispatcharr-fs-time"
        style={{ color: 'white', fontSize: 13, minWidth: 46, textAlign: 'right' }}
      >
        0:00
      </span>

      <div
        ref={trackRef}
        className="dispatcharr-fs-track"
        onPointerMove={handlePointerMove}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={() => {
          if (!draggingRef.current) hideTooltip();
        }}
        style={{
          position: 'relative',
          flex: '1 1 0',
          minWidth: 0,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          touchAction: 'none',
        }}
      >
        {/* the readout the native timeline could not give us */}
        <div
          ref={tooltipRef}
          className="dispatcharr-fs-tip"
          style={{
            position: 'absolute',
            // DISPATCHARR_SEEKBAR_TOOLTIP_LIFT_R1
            bottom: 'calc(100% + 4px)',
            transform: 'translateX(-50%)',
            zIndex: 6,
            padding: '2px 6px',
            borderRadius: 4,
            background: 'rgba(0,0,0,0.85)',
            color: 'white',
            fontSize: 11,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            opacity: 0,
            transition: 'opacity 0.12s ease',
          }}
        >
          0:00
        </div>

        <div
          className="dispatcharr-fs-rail"
          style={{
            position: 'relative',
            width: '100%',
            height: 8,
            borderRadius: 3,
            background: 'rgba(255,255,255,0.34)',
          }}
        >
          <div
            ref={fillRef}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '0%',
              borderRadius: 3,
              background: '#4c8dff',
            }}
          />
          <div
            ref={thumbRef}
            className="dispatcharr-fs-thumb"
            style={{
              position: 'absolute',
              left: '0%',
              top: '50%',
              width: 16,
              height: 16,
              marginLeft: -8,
              marginTop: -8,
              borderRadius: '50%',
              background: '#ffffff',
              boxShadow: '0 0 3px rgba(0,0,0,0.6)',
              pointerEvents: 'none',
            }}
          />
        </div>
      </div>

      <span
        ref={totalRef}
        className="dispatcharr-fs-time"
        style={{ color: 'white', fontSize: 13, minWidth: 46 }}
      >
        0:00
      </span>

      {/* Real volume control: the icon toggles mute, the strip sets the level.
          It stays collapsed until hovered so the bar keeps its width on a
          narrow player. DISPATCHARR_SEEKBAR_VOLUME_R3 */}
      <div
        ref={volumeWrapRef}
        onPointerEnter={() => setVolumeSlotOpen(true)}
        onPointerLeave={() => {
          if (!volumeDraggingRef.current) setVolumeSlotOpen(false);
        }}
        style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
      >
        <button
          type="button"
          className="dispatcharr-fs-btn dispatcharr-fs-mute"
          title="Mute / unmute — drag or scroll the strip to set the level"
          onClick={toggleMute}
          style={{
            border: 0,
            background: 'transparent',
            color: 'white',
            cursor: 'pointer',
            fontSize: 17,
            minWidth: 30,
          }}
        >
          <span ref={muteIconRef}>🔊</span>
        </button>

        <div
          ref={volumeSlotRef}
          style={{
            width: 0,
            overflow: 'hidden',
            transition: 'width 0.18s ease',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div
            ref={volumeBarRef}
            className="dispatcharr-fs-volbar"
            onPointerDown={handleVolumePointerDown}
            onPointerMove={handleVolumePointerMove}
            onPointerUp={handleVolumePointerUp}
            onPointerCancel={handleVolumePointerUp}
            style={{
              position: 'relative',
              width: 46,
              height: 18,
              marginLeft: 2,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              touchAction: 'none',
            }}
          >
            <div
              className="dispatcharr-fs-volrail"
              style={{
                position: 'relative',
                width: '100%',
                height: 5,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.34)',
              }}
            >
              <div
                ref={volumeFillRef}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '100%',
                  borderRadius: 3,
                  background: '#ffffff',
                }}
              />
              <div
                ref={volumeKnobRef}
                className="dispatcharr-fs-volknob"
                style={{
                  position: 'absolute',
                  left: '100%',
                  top: '50%',
                  width: 11,
                  height: 11,
                  marginLeft: -5.5,
                  marginTop: -5.5,
                  borderRadius: '50%',
                  background: '#ffffff',
                  boxShadow: '0 0 3px rgba(0,0,0,0.6)',
                  pointerEvents: 'none',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {onFullscreen && (
        <button
          type="button"
          className="dispatcharr-fs-btn dispatcharr-fs-full"
          title="Fullscreen"
          onClick={onFullscreen}
          style={{
            border: 0,
            background: 'transparent',
            color: 'white',
            cursor: 'pointer',
            fontSize: 22,
            minWidth: 34,
          }}
        >
          ⛶
        </button>
      )}
    </div>
  );
}
